import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { LoginPage } from "./components/LoginPage";
import { AuthCallback } from "./components/AuthCallback";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { AppLayout } from "./routes/AppLayout";
import { WorkflowLayout } from "./routes/WorkflowLayout";
import { WorkflowListView } from "./routes/WorkflowListView";
import { WorkflowView } from "./routes/WorkflowView";
import { SettingsLayout } from "./routes/SettingsLayout";
import { GeneralSettingsView } from "./routes/GeneralSettingsView";
import { WorkflowSettingsView } from "./routes/WorkflowSettingsView";
import { AgentListView } from "./routes/AgentListView";
import { AgentCreateView } from "./routes/AgentCreateView";
import { AgentEditView } from "./routes/AgentEditView";
import { MCPServersView } from "./routes/MCPServersView";
import { AgentDetailView } from "./routes/AgentDetailView";
import "./index.css";

// Enable React strict mode in development
const StrictModeWrapper = ({ children }: { children: React.ReactNode }) => {
  if (process.env.NODE_ENV === "development") {
    return <React.StrictMode>{children}</React.StrictMode>;
  }
  return <>{children}</>;
};

// Create router configuration
const router = createBrowserRouter([
  { 
    path: "/login", 
    element: <LoginPage />,
    errorElement: <ErrorBoundary />
  },
  { 
    path: "/auth/callback", 
    element: <AuthCallback />,
    errorElement: <ErrorBoundary />
  },
  {
    path: "/app",
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { 
        index: true, 
        element: <WorkflowListView />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "new", 
        element: <WorkflowView />,
        errorElement: <ErrorBoundary />
      },
      {
        path: "settings",
        element: <SettingsLayout />,
        errorElement: <ErrorBoundary />,
        children: [
          { 
            index: true, 
            element: <GeneralSettingsView />,
            errorElement: <ErrorBoundary />
          },
          { 
            path: "workflow", 
            element: <WorkflowSettingsView />,
            errorElement: <ErrorBoundary />
          },
          { 
            path: "agents", 
            element: <AgentListView />,
            errorElement: <ErrorBoundary />
          },
          { 
            path: "agents/new", 
            element: <AgentCreateView />,
            errorElement: <ErrorBoundary />
          },
          { 
            path: "agents/edit/:agentId", 
            element: <AgentEditView />,
            errorElement: <ErrorBoundary />
          },
          { 
            path: "mcp", 
            element: <MCPServersView />,
            errorElement: <ErrorBoundary />
          }
        ]
      },
      {
        path: "workflow/:workflowId",
        element: <WorkflowLayout />,
        errorElement: <ErrorBoundary />,
        children: [
          { 
            index: true, 
            element: <WorkflowView />,
            errorElement: <ErrorBoundary />
          },
          { 
            path: "agent/:agentId", 
            element: <AgentDetailView />,
            errorElement: <ErrorBoundary />
          }
        ]
      }
    ]
  },
  { 
    path: "/", 
    element: <Navigate to="/app" replace />,
    errorElement: <ErrorBoundary />
  }
]);

// Create root and render app
const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container not found");
}

const root = ReactDOM.createRoot(container);

root.render(
  <StrictModeWrapper>
    <ErrorBoundary>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ErrorBoundary>
  </StrictModeWrapper>,
);

// Hot module replacement for development
if (import.meta.hot) {
  import.meta.hot.accept();
}

// Performance monitoring
if ("performance" in window && "mark" in performance) {
  performance.mark("app-render-start");

  // Mark when React has finished initial render
  setTimeout(() => {
    performance.mark("app-render-end");
    performance.measure("app-render", "app-render-start", "app-render-end");

    const measure = performance.getEntriesByName("app-render")[0];
    if (measure) {
      console.log(`React app render time: ${measure.duration.toFixed(2)}ms`);
    }
  }, 0);
}

// Service worker registration for PWA capabilities
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        console.log("SW registered: ", registration);
      })
      .catch((registrationError) => {
        console.log("SW registration failed: ", registrationError);
      });
  });
}
